import type { IGPTQuestion } from 'widgets/GPTSettings/types';
import type {
	GPTAnswers,
	GptEngine,
	GPTOptionAnyOfNumber,
	GPTOptionParams,
	GPTOptions,
	GPTOptionsModelAllOf,
	GPTOptionsModelWithoutAllOf,
	GPTOptionsProperties,
	IRuleTagForGpt,
} from 'store/gpt/types';
import type { GptEngineParams } from '../gptService';
import { setNumOrZero } from 'shared/utils/setNumOrZero';
import { GptSettingsKeys, GptSettingsRawKeys } from './types';

// Восстанавливаем порядок ответов, чтобы он совпадал
// с порядком вопросов в UI
// "временный" костыль, потому что АПИ ответы на вопросы-комментарии
// присылает массивом из строк
export const getAnswersFromRuleResponse = (answers: string[], tags: IRuleTagForGpt[][], questions?: IGPTQuestion[]) => {
	let answerInd = 0;
	return questions
		? questions.map((question) => {
				const data: GPTAnswers = {
					comments: [],
					tags: [],
				};

				if (question.isComment) {
					const result = answers[answerInd];
					answerInd++;
					data.comments.push(result);
				}

				if (tags.length) {
					tags.forEach((tag) =>
						tag.forEach((item) => {
							if (question.tagName === item.tagName) {
								data.tags.push(item.tagValue || 'no data');
							}
						}),
					);
				}
				return data;
		  })
		: [];
};

export const getOnlyAnswersFromRuleResponse = (answers: string[]) => {
	return answers.length
		? answers.map((answer) => {
				const data: GPTAnswers = {
					comments: [],
					tags: [],
				};
				data.comments.push(answer);
				return data;
		  })
		: [];
};

const getAdditionalParams = (properties: GPTOptionsProperties) => {
	const params: Record<string, GPTOptionParams> = {};

	const gptSettingsDict: Record<string, GptSettingsKeys> = {
		frequency_penalty: GptSettingsKeys.FrequencyPenalty,
		presence_penalty: GptSettingsKeys.PresencePenalty,
		temperature: GptSettingsKeys.Temperature,
		repetition_penalty: GptSettingsKeys.RepetitionPenalty,
	};

	(Object.keys(properties) as Array<keyof GPTOptionsProperties>).forEach((item) => {
		if (!(item in gptSettingsDict)) return;

		const key = gptSettingsDict[item];
		const property = properties[item] as GPTOptionParams;

		if (
			item === (GptSettingsRawKeys.RepetitionPenalty as string) &&
			'anyOf' in property &&
			Array.isArray(property.anyOf)
		) {
			const numberObj = property.anyOf.find((el: GPTOptionAnyOfNumber) => el.type === 'number') || {};
			params[key] = {
				default: setNumOrZero(property.default ?? numberObj.default),
				minimum: setNumOrZero(numberObj.minimum),
				maximum: setNumOrZero(numberObj.maximum) || 2,
				title: property.title ?? '',
				type: numberObj.type ?? 'number',
			};
			return;
		}

		params[key] = {
			default: setNumOrZero(property.default),
			minimum: setNumOrZero((property as any).minimum),
			maximum: setNumOrZero((property as any).maximum),
			title: property.title ?? '',
			type: property.type ?? 'number',
		};
	});

	return params;
};

export const getEngineOptions = (response: GPTOptions, engine: GptEngine): GptEngineParams => {
	const modelSchema = response.properties.model;
	let ref: string[] = [];
	if ('allOf' in modelSchema) {
		ref = (modelSchema as GPTOptionsModelAllOf).allOf[0].$ref.split('/');
	} else if ('$ref' in modelSchema) {
		ref = (modelSchema as GPTOptionsModelWithoutAllOf).$ref.split('/');
	}

	const model = ref[ref.length - 1];
	const modelProps = response.$defs[model];

	const additionalParams = getAdditionalParams(response.properties);

	return {
		currentGptEngine: engine,
		models: modelProps?.enum || [modelProps?.const],
		defaultModel: response.properties.model.default,
		additionalParams,
	};
};
